import React from "react";
import {
  Layout,
  Card,
  TextContainer,
  FormLayout,
  TextField,
  TextStyle,
  Banner,
  Checkbox,
} from "@shopify/polaris";
import get from "lodash/get";
import without from "lodash/without";
import { useFormikContext } from "formik";
import { useShop } from "providers/ShopProvider";

const merchantEmailTemplates = [
  {
    key: "completed",
    label: "ID verification completed",
    helpText: "Sent when a customer successfully passes ID verification.",
  },
  {
    key: "failed",
    label: "ID verification failed",
    helpText:
      "Sent when a customer fails automatic ID verification and needs manual review.",
  },
  {
    key: "manually_approved",
    label: "Manually approved",
    helpText: "Sent when a staff member manually approves an ID check.",
  },
  {
    key: "underage",
    label: "Underage customer detected",
    helpText:
      "Sent when a customer is flagged as underage based on your age requirements.",
  },
];

export default function CheckCompletedNotifications() {
  const { values, setFieldValue, errors } = useFormikContext();
  const { shop } = useShop();

  const hasContactEmails =
    values.contactEmails?.filter(Boolean).length > 0;
  const fallbackEmail = shop?.contactEmail || shop?.email;

  return (
    <>
      <Layout.AnnotatedSection
        title="ID Check Notifications"
        description={
          <>
            <p>
              These email addresses will receive notifications when customers
              complete ID checks.
            </p>
            <br />
            <p>
              Add your team members emails to keep them updated when customers
              pass or fail ID checks.
            </p>
          </>
        }
      >
        <Card
          fullWidth
          title="Email Addresses"
          primaryFooterAction={{
            content: "Add email",
            onAction: () => {
              setFieldValue("contactEmails", [...values.contactEmails, ""]);
            },
          }}
        >
          <Card.Section>
            <p className="ri-my-3">
              <TextStyle variation="subdued">
                When an ID check event occurs, notification emails will be sent
                to these email addresses.
              </TextStyle>
            </p>
            {values.contactEmails.map((contactEmail, i) => (
              <FormLayout key={i}>
                <TextField
                  label="Email"
                  type="email"
                  error={get(errors, `contactEmails[${i}]`)}
                  value={contactEmail}
                  onChange={(value) => {
                    setFieldValue(`contactEmails[${i}]`, value);
                  }}
                  clearButton={i != 0}
                  onClearButtonClick={(email) => {
                    const newContactEmails = without(
                      values.contactEmails,
                      values.contactEmails[i]
                    );
                    setFieldValue("contactEmails", newContactEmails);
                  }}
                />
              </FormLayout>
            ))}
            {!hasContactEmails && fallbackEmail && (
              <div style={{ marginTop: "1rem" }}>
                <Banner status="info">
                  No team emails are set. Notifications will be sent to your
                  store's contact email: <strong>{fallbackEmail}</strong>
                </Banner>
              </div>
            )}
          </Card.Section>
        </Card>
      </Layout.AnnotatedSection>

      <Layout.AnnotatedSection
        title="Notifications"
        description="Choose which email notifications your team receives."
      >
        <Card fullWidth>
          <Card.Section>
            <TextContainer>
              <TextStyle variation="subdued">
                Disabling a notification type will stop those emails for all team
                members.
              </TextStyle>
            </TextContainer>
            <div style={{ marginTop: "1rem" }}>
              {merchantEmailTemplates.map((template) => (
                <div key={template.key} style={{ marginBottom: "0.75rem" }}>
                  <Checkbox
                    label={template.label}
                    helpText={template.helpText}
                    checked={
                      get(
                        values,
                        `enabledMerchantEmails.check.${template.key}`
                      ) !== false
                    }
                    onChange={(checked) => {
                      setFieldValue(
                        `enabledMerchantEmails.check.${template.key}`,
                        checked
                      );
                    }}
                  />
                </div>
              ))}
            </div>
          </Card.Section>
        </Card>
      </Layout.AnnotatedSection>
    </>
  );
}
